import React from 'react';
import { TrashIcon } from './icons';
import { roleColors, TeamMember } from './types';
import { formatDate, getInitials } from './utils';

interface MembersTabProps {
  members: TeamMember[];
  isOwner: boolean;
  onRoleChange: (userId: string, newRole: 'admin' | 'member') => void;
  onRemoveMember: (member: TeamMember) => void;
}

export const MembersTab: React.FC<MembersTabProps> = ({
  members,
  isOwner,
  onRoleChange,
  onRemoveMember,
}) => {
  return (
    <div className="space-y-4">
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">
            Team Members ({members.length})
          </h3>
        </div>

        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {members.map((member) => (
            <div key={member.id} className="px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-linear-to-br from-teal-500 to-blue-600 flex items-center justify-center text-white font-semibold text-sm">
                  {getInitials(member.firstName, member.email)}
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {member.firstName || member.email}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{member.email}</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                    Joined {formatDate(member.joinedAt)}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {member.role === 'owner' ? (
                  <span className={`text-xs px-2 py-1 rounded ${roleColors.owner}`}>Owner</span>
                ) : isOwner ? (
                  <select
                    value={member.role}
                    onChange={(e) =>
                      onRoleChange(member.userId, e.target.value as 'admin' | 'member')
                    }
                    className="text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                  >
                    <option value="admin">Admin</option>
                    <option value="member">Member</option>
                  </select>
                ) : (
                  <span className={`text-xs px-2 py-1 rounded ${roleColors[member.role]}`}>
                    {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
                  </span>
                )}

                {isOwner && member.role !== 'owner' && (
                  <button
                    onClick={() => onRemoveMember(member)}
                    className="p-1.5 text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                    title="Remove member"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
